/**
 * useArtworks Hook
 * Lädt und cached Artwork-Daten
 */

import { useState, useEffect } from 'react'
import { ContentIndex, Artwork, Category, MenuSection } from '@/types/artwork'

export function useArtworks() {
  const [data, setData] = useState<ContentIndex | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    async function loadContent() {
      try {
        const response = await fetch('/content/index.json?v=' + Date.now())
        if (!response.ok) {
          throw new Error('Failed to load content')
        }
        const content = await response.json()
        setData(content)
      } catch (err) {
        setError(err as Error)
      } finally {
        setLoading(false)
      }
    }

    loadContent()
  }, [])

  const getArtworkById = (id: string): Artwork | null => {
    return data?.artworks.find((a) => a.id === id) || null
  }

  const getArtworkBySlug = (slug: string): Artwork | null => {
    return data?.artworks.find((a) => a.slug === slug) || null
  }

  const getArtworkByCategoryAndSlug = (
    categorySlug: string,
    slug: string
  ): Artwork | null => {
    const category = data?.categories.find((c) => c.slug === categorySlug)
    if (!category) return null
    return (
      data?.artworks.find(
        (a) => a.categoryId === category.id && a.slug === slug
      ) || null
    )
  }

  const getArtworksByCategory = (categoryId: string): Artwork[] => {
    return data?.artworks.filter((a) => a.categoryId === categoryId) || []
  }

  const getCategoryById = (id: string): Category | null => {
    return data?.categories.find((c) => c.id === id) || null
  }

  const getFeaturedArtworks = (): Artwork[] => {
    if (!data) return []
    return data.featured
      .map((id) => getArtworkById(id))
      .filter((a): a is Artwork => a !== null)
  }

  const getSection = (slug: string): MenuSection | null => {
    return data?.menuSections?.find((s) => s.slug === slug) || null
  }

  return {
    data,
    loading,
    error,
    artworks: data?.artworks || [],
    categories: data?.categories || [],
    menuSections: data?.menuSections || [],
    getArtworkById,
    getArtworkBySlug,
    getArtworkByCategoryAndSlug,
    getArtworksByCategory,
    getCategoryById,
    getFeaturedArtworks,
    getSection,
  }
}
